import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function DiegoPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 items-start">
            <div className="md:col-span-3">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Diego</h2>
              <h3 className="text-xl text-gray-600 mb-6">Advanced Upper Limb Rehabilitation System</h3>
              
              <p className="text-base text-gray-700 leading-relaxed mb-5">
                Diego is a sophisticated robotic rehabilitation system designed for comprehensive upper body therapy. With its dual arm support mechanism and suspension harness, Diego enables patients to perform functional arm movements in a safe, controlled environment while receiving real-time feedback and guidance.
              </p>
              
              <p className="text-base text-gray-700 leading-relaxed">
                The system combines advanced robotics with interactive therapy software to deliver engaging, effective rehabilitation sessions that accelerate recovery and improve functional outcomes for patients with upper limb impairments.
              </p>
            </div>
            
            <div className="md:col-span-2 rounded-lg overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/c8240ebf56be588be75a56b670284bec.jpg"
                alt="Diego Rehabilitation System"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-10">Key Features</h3>
          
          <div className="grid md:grid-cols-3 gap-x-12 gap-y-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Dual Arm Support System</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Advanced bilateral arm support with independent control for each arm, enabling coordinated movement training
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Adjustable Suspension</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Customizable weight support system that can be adjusted to patient needs and progression levels
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Interactive Therapy Games</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Engaging digital exercises that motivate patients while providing targeted upper limb rehabilitation
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Real-time Feedback</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Instant performance metrics and movement analysis to guide therapy sessions and track improvements
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Personalized Programs</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Customizable therapy protocols tailored to individual patient conditions and recovery goals
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Safety Features</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Built-in safety mechanisms including emergency stop and controlled movement ranges
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Applications */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Clinical Applications</h3>
          
          <div className="space-y-5">
            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Stroke Recovery</h4>
              <p className="text-gray-700">
                Helps restore arm and shoulder function after stroke through repetitive task-oriented training
              </p>
            </div>

            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Spinal Cord Injury</h4>
              <p className="text-gray-700">
                Supports upper limb rehabilitation for patients with incomplete spinal cord injuries
              </p>
            </div>

            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Traumatic Brain Injury</h4>
              <p className="text-gray-700">
                Assists in regaining arm coordination and strength following brain trauma
              </p>
            </div>

            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Multiple Sclerosis</h4>
              <p className="text-gray-700">
                Maintains and improves upper extremity function in MS patients
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Post-Surgical Rehabilitation</h4>
              <p className="text-gray-700">
                Facilitates recovery after shoulder, arm, or upper body surgical procedures
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Therapy Process */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Therapy Process</h3>
          
          <div className="space-y-6">
            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-lg font-bold text-gray-400">01</span>
                <h4 className="font-semibold text-gray-900">Initial Assessment</h4>
              </div>
              <p className="text-gray-700 ml-9">
                Therapist evaluates patient range of motion, strength, and functional capabilities
              </p>
            </div>

            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-lg font-bold text-gray-400">02</span>
                <h4 className="font-semibold text-gray-900">Setup & Calibration</h4>
              </div>
              <p className="text-gray-700 ml-9">
                Device is adjusted to patient body dimensions and support levels are configured
              </p>
            </div>

            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-lg font-bold text-gray-400">03</span>
                <h4 className="font-semibold text-gray-900">Guided Training</h4>
              </div>
              <p className="text-gray-700 ml-9">
                Patient performs exercises with real-time guidance and adjustable assistance
              </p>
            </div>

            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-lg font-bold text-gray-400">04</span>
                <h4 className="font-semibold text-gray-900">Progress Monitoring</h4>
              </div>
              <p className="text-gray-700 ml-9">
                System tracks performance data and therapist adjusts program based on improvements
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Why Choose Diego?</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Intensive Training</h4>
              <p className="text-gray-700 text-sm">
                Enables high-repetition exercises that accelerate neuroplasticity and recovery
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Reduced Therapist Burden</h4>
              <p className="text-gray-700 text-sm">
                Mechanical support reduces physical strain on therapists during sessions
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Objective Measurement</h4>
              <p className="text-gray-700 text-sm">
                Precise tracking of movement quality and progress over time
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Patient Motivation</h4>
              <p className="text-gray-700 text-sm">
                Interactive games and visual feedback keep patients engaged in therapy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
